import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FiArrowLeft, FiList} from "react-icons/fi";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import PageHeader from "../../../../components/common/pageheader/PageHeader";
import Loader from "../../../../components/common/Loader/Loader";
import styles from "./ExamAttemptDetailStudentPage.module.css";

import {getExamAttemptAnswerGroups, getExamAttemptAnswersByGroup} from "../../../../services/examAttemptsApi";
import {renderAttemptStatusBadge} from "../../../../utils/statusUtil";
import QuestionContent from "../../../../components/common/QuestionContent/QuestionContent";
import QuestionVideo from "../../../../components/common/QuestionVideo/QuestionVideo";
import QuestionExplanation from "../../../../components/common/QuestionExplanation/QuestionExplanation";
import SubjectGroupedAnswers from "./SubjectGroupedAnswers";
import OpenAnswerReview from "../../../../components/common/OpenAnswerReview/OpenAnswerReview";

const ExamAttemptDetailStudentPage = () => {
    const {t} = useTranslation();
    const {attemptId} = useParams();
    const navigate = useNavigate();

    const [answerGroups, setAnswerGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const location = useLocation();

    useEffect(() => {
        if (!attemptId) return;

        const load = async () => {
            setLoading(true);
            setError("");

            try {
                // Only the collapsed subject/topic headers; a group's answers load when opened.
                const groupsRes = await getExamAttemptAnswerGroups(attemptId);
                setAnswerGroups(Array.isArray(groupsRes) ? groupsRes : []);
            } catch (e) {
                const msg = e?.response?.data?.message || t("pages.examAttemptDetail.loadError");
                setError(msg);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [attemptId, t]);

    // Fallback score from the loaded answers when navigation state is missing
    // (hard refresh / direct URL) — otherwise the header shows "undefined %".
    const hasState = !!location.state;
    // Taken from the group headers rather than the answer cards, so the fallback score is still the
    // whole attempt even though no group has been opened yet. The counts are the backend's
    // authoritative per-answer correctness (MCQ from the options, OPEN from the AI / teacher).
    const total = answerGroups.reduce((n, g) => n + (Number(g.questionCount) || 0), 0);
    const correctCount = answerGroups.reduce((n, g) => n + (Number(g.correctCount) || 0), 0);
    const computedScore = total > 0 ? Math.round((correctCount / total) * 10000) / 100 : null;
    const displayScore = location.state?.score != null ? location.state.score : computedScore;

    return (
        <div className={styles.container}>
            <PageHeader
                title={t("pages.examAttemptDetail.title")}
                icon={<FiList/>}
                description={t("pages.examAttemptDetail.subtitle")}
            />

            <div className={styles.headerRow}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <FiArrowLeft/>
                    <span>{t("common.back")}</span>
                </button>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {loading ? (
                <Loader fullscreen/>
            ) : (
                <div className={styles.sections}>
                    <div className={styles.card}>
                        <div className={styles.infoText}>
                            {t("pages.examAttemptDetail.scoreLabel")}: {displayScore != null ? `${displayScore} %` : "—"}
                            {hasState && renderAttemptStatusBadge(location.state?.finalStatus)}
                        </div>
                        {total > 0 && (
                            <div className={styles.resultTitle}>{t("pages.examAttemptDetail.correctCount", { correct: correctCount, total })}</div>
                        )}
                        {hasState && <div className={styles.resultTitle}>{t("common.status")}: {renderAttemptStatusBadge(location.state?.attemptStatus)}</div>}
                        {location.state?.cancelResultReason && <div className={styles.resultTitle}>{t("pages.examAttemptDetail.cancelReason")}: {location.state?.cancelResultReason}</div>}
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardTitle}>{t("pages.examAttemptDetail.questions")}</div>

                        {answerGroups.length === 0 ? (
                            <div className={styles.infoText}>{t("pages.examAttemptDetail.noAnswers")}</div>
                        ) : (
                            <div className={styles.answersList}>
                                <SubjectGroupedAnswers
                                    groups={answerGroups}
                                    loadGroup={(group) => getExamAttemptAnswersByGroup(attemptId, {
                                        subjectId: group.subjectId,
                                        topicId: group.topicId,
                                    })}
                                    renderQuestion={(item, number) => {
                                    const q = item?.question;
                                    const qKey = q?.id ?? item?.id;

                                    return (
                                        <div key={qKey} className={q?.studentAnswered ? styles.answerBlock : styles.unansweredBlock}>
                                            <div className={styles.qText}>
                                                <span className={styles.qNumber}>{number}.</span>
                                                <QuestionContent text={q?.text || t("pages.examAttemptDetail.questionFallback")} />
                                            </div>

                                            <QuestionVideo playbackUrl={q?.videoPlaybackUrl} thumbnailUrl={q?.videoThumbnailUrl}/>

                                            <QuestionExplanation explanation={q?.explanation}/>

                                            {q?.type === "OPEN" ? (
                                                <OpenAnswerReview question={q} selfView={true} />
                                            ) : (
                                            <div className={styles.options}>
                                                {(q?.options || []).map((opt) => (
                                                    <div
                                                        key={opt.id}
                                                        className={[
                                                            styles.optionRow,
                                                            opt.correct && styles.correctOption,
                                                            opt.studentSelected && !opt.correct && styles.wrongOption,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ")}
                                                    >
                                                        <QuestionContent text={opt.optionText} />
                                                        {opt.attachmentUrl && (
                                                            <img
                                                                src={opt.attachmentUrl}
                                                                alt="option"
                                                                className={styles.imagePreview}
                                                            />
                                                        )}
                                                        {opt.correct && (
                                                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "#047857" }}>
                                                                ✓ {t("pages.examAttemptDetail.correctTag")}
                                                            </span>
                                                        )}
                                                        {opt.studentSelected && (
                                                            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: opt.correct ? "#047857" : "#b91c1c" }}>
                                                                {t("pages.examAttemptDetail.yourAnswerTag")}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            )}
                                        </div>
                                    );
                                }} />

                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExamAttemptDetailStudentPage;
